/**
 * EntreprisePortalControls — Batch #14.313 (ex-PortalControls du hub Entrepreneurs).
 * Interrupteur « tuile Entreprise » du portail client + fonctions offertes (Tout / Dépenses / Kilométrage). Affiché dans le hub Tenue de livres.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Globe, Loader2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "../entrepreneurs/_shared";

const SCOPES = [["all", "Tout"], ["expenses", "Dépenses"], ["mileage", "Kilométrage"]];

export default function EntreprisePortalControls({ master }) {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!master?.id) return;
    setSettings(null);
    axios.get(`${API}/admin/entrepreneurs/${master.id}/settings`, { headers: authH() })
      .then((r) => setSettings(r.data))
      .catch(() => setSettings({ portal_tile_enabled: false, portal_scope: "all" }));
  }, [master?.id]);

  const toggle = async () => {
    setBusy(true);
    try {
      const next = !settings.portal_tile_enabled;
      const r = await axios.get(`${API}/masters/${master.id}/portal-permissions`, { headers: authH() });
      const members = ["primary", ...(r.data?.has_secondary ? ["secondary"] : [])];
      await Promise.all(members.map((member) => axios.put(`${API}/masters/${master.id}/portal-permissions`,
        { member, permissions: { travail_autonome: next } }, { headers: authH() })));
      setSettings((s) => ({ ...s, portal_tile_enabled: next }));
      toast.success(next ? "Tuile « Entreprise » visible dans le portail du client" : "Tuile « Entreprise » retirée du portail");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const setScope = async (scope) => {
    setBusy(true);
    try {
      const { portal_tile_enabled, ...body } = settings;
      await axios.put(`${API}/admin/entrepreneurs/${master.id}/settings`, { ...body, portal_scope: scope }, { headers: authH() });
      setSettings((s) => ({ ...s, portal_scope: scope }));
      toast.success(`Fonctions client : ${SCOPES.find(([k]) => k === scope)?.[1]}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const enabled = settings?.portal_tile_enabled;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="ent-portal-controls">
      <button type="button" onClick={toggle} disabled={busy || !settings}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition ${enabled ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}
        data-testid="ent-portal-tile-toggle">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
        Tuile portail « Entreprise » : {!settings ? "…" : enabled ? "activée" : "désactivée"}
      </button>
      <div className="inline-flex rounded-lg border border-slate-300 bg-white overflow-hidden text-xs" title="Fonctions offertes au client dans son portail">
        {SCOPES.map(([k, label]) => (
          <button key={k} type="button" disabled={busy || !settings} onClick={() => setScope(k)}
            className={`px-2.5 py-1.5 font-semibold transition ${settings?.portal_scope === k ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            data-testid={`ent-portal-scope-${k}`}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
